/**
 * Sistem Ayarları › SMS Şablonları (backlog #637, backend #634): sistemin KENDİ gönderdiği SMS'lerin (OTP_MFA, OTP_PANEL, PANEL_DAVET, KVKK_DAVET, KART_TESLIM ...)
 * Türkçe ve İngilizce metinleri. E-posta sistem şablonlarının SMS karşılığı; duyurular şablon değil serbest metindir (#643).
 *
 * Düzenleme penceresinde:
 *   · yer tutucular ({{KOD}}, {{LINK}} ...) tıklayınca imlecin olduğu alana eklenir; zorunlu olanlar (OTP için KOD) metinde yoksa Kaydet açılmaz
 *   · CANLI SAYAÇ: karakter, satıcının SAYDIĞI karakter (bazıları iki sayılır), parça sayısı, OTP için 160 sınırı; yer tutucular örnek değerle doldurulup ÖLÇÜLÜR
 *   · satıcının tablosunda karşılığı olmayan karakter (çift tırnak, ters bölü, [ ] |, Latin olmayan) ve bilinmeyen yer tutucu Kaydet'i kapatır (backend de reddeder)
 *   · "Önizle" sunucunun örnek değerlerle üretilmiş metnini ve parça sayısını gösterir
 * İngilizce metin boş bırakılabilir: o zaman İngilizce okuyana Türkçe gider. Kaydetmek SYSTEM_SETTINGS_MANAGE ister.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, CardBody, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import SmsSayfa from './SmsSayfa';
import SmsMetinSayaci from './SmsMetinSayaci';
import { listSystemSmsTemplates, previewSmsTemplate, updateSmsTemplate } from '../../../../services/smsAyarService';
import { OTP_SINIRI, SMS_MAX, doldur, olc, yerTutucular } from '../../../../services/smsMetin';

const VARSAYILAN_ORNEK = {
  AD: 'Ayse', KOD: '123456', SIFRE: '12345678', LINK: 'https://fds.sakagrup.com/p/AbCdEfGhIjKlMnOpQrSt', FIRMA: 'ORNEK FIRMA', FUAR: 'ORNEK FUAR 2026', GECERLILIK: '48 saat',
};

/** API yer tutucuyu `{ name, token, description, sample }` ya da yalın ad olarak verebilir. */
export function yerTutucuListesi(sablon) {
  return (sablon.placeholders || []).map((p) => (typeof p === 'string' ? { name: p, sample: VARSAYILAN_ORNEK[p] } : { ...p, name: p.name || String(p.token || '').replace(/[{}\s]/g, ''), sample: p.sample ?? VARSAYILAN_ORNEK[p.name] }));
}

/** Kaydetmeyi engelleyen sorunlar (boş = sorun yok). `kodlar`: bu dil için bu fonksiyonun döndürdüğü anahtarlar `sms:sablonlar.sorun.<kod>` olarak çevrilir. */
export function metinSorunlari(metin, { otp, izinli, zorunlu, bosOlabilir }) {
  const sorunlar = [];
  const m = metin || '';
  if (!m.trim()) return bosOlabilir ? sorunlar : ['bos'];
  const ornek = Object.fromEntries(izinli.filter((p) => p.sample !== undefined).map((p) => [p.name, p.sample]));
  const o = olc(doldur(m, ornek));
  if (o.weighted > (otp ? OTP_SINIRI : SMS_MAX)) sorunlar.push(otp ? 'otpUzun' : 'uzun');
  if (o.desteklenmeyen.length) sorunlar.push('karakter');
  const kullanilan = yerTutucular(m);
  if (kullanilan.some((ad) => !izinli.some((p) => p.name === ad))) sorunlar.push('bilinmeyenYerTutucu');
  if (zorunlu.some((ad) => !kullanilan.includes(ad))) sorunlar.push('zorunluEksik');
  return sorunlar;
}

const SmsSablonlariPage = () => {
  const { t } = useTranslation('sms');
  const [liste, setListe] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [mesaj, setMesaj] = useState(null);
  const [duzen, setDuzen] = useState(null);   // { sablon, tr, en, etkin }
  const [formHatasi, setFormHatasi] = useState(null);
  const [kaydediyor, setKaydediyor] = useState(false);
  const [onizleme, setOnizleme] = useState(null);   // { dil, sonuc|hata, yukleniyor }
  const trRef = useRef(null);
  const enRef = useRef(null);
  const sonOdak = useRef('tr');

  async function yukle() {
    try {
      setListe(await listSystemSmsTemplates());
      setHata(null);
    } catch (e) {
      setHata(e.message);
    } finally {
      setYukleniyor(false);
    }
  }

  useEffect(() => {
    let iptal = false;
    async function calistir() {
      try {
        const l = await listSystemSmsTemplates();
        if (!iptal) setListe(l);
      } catch (e) {
        if (!iptal) setHata(e.message);
      } finally {
        if (!iptal) setYukleniyor(false);
      }
    }
    calistir();
    return () => { iptal = true; };
  }, []);

  const ac = (s) => { setFormHatasi(null); setOnizleme(null); setDuzen({ sablon: s, tr: s.text_tr || '', en: s.text_en || '', etkin: s.active !== false }); };

  const otp = !!duzen && (duzen.sablon.otp === true || duzen.sablon.category === 'OTP');
  const izinli = duzen ? yerTutucuListesi(duzen.sablon) : [];
  const zorunlu = duzen ? (duzen.sablon.required || (otp ? ['KOD'] : [])) : [];
  const ornekler = Object.fromEntries(izinli.map((p) => [p.name, p.sample]).filter(([, v]) => v !== undefined));
  const trSorun = duzen ? metinSorunlari(duzen.tr, { otp, izinli, zorunlu, bosOlabilir: false }) : [];
  const enSorun = duzen ? metinSorunlari(duzen.en, { otp, izinli, zorunlu, bosOlabilir: true }) : [];
  const kaydedilemez = !duzen || trSorun.length > 0 || enSorun.length > 0;

  function ekle(ad) {
    const dil = sonOdak.current;
    const el = (dil === 'en' ? enRef : trRef).current;
    const belirtec = `{{${ad}}}`;
    setDuzen((d) => {
      const deger = d[dil];
      const bas = el && el.selectionStart !== undefined ? el.selectionStart : deger.length;
      const son = el && el.selectionEnd !== undefined ? el.selectionEnd : bas;
      return { ...d, [dil]: deger.slice(0, bas) + belirtec + deger.slice(son) };
    });
  }

  async function kaydet() {
    if (kaydedilemez) return;
    setKaydediyor(true);
    setFormHatasi(null);
    try {
      await updateSmsTemplate(duzen.sablon.key, { textTr: duzen.tr, textEn: duzen.en, active: duzen.etkin });
      setDuzen(null);
      setMesaj(t('sablonlar.kaydedildi'));
      await yukle();
    } catch (e) {
      setFormHatasi(e.message);
    } finally {
      setKaydediyor(false);
    }
  }

  async function onizle(dil) {
    setOnizleme({ dil, yukleniyor: true });
    try {
      setOnizleme({ dil, sonuc: await previewSmsTemplate(duzen.sablon.key, dil) });
    } catch (e) {
      setOnizleme({ dil, hata: e.message });
    }
  }

  const sorunMetni = (sorunlar) => sorunlar.map((k) => <div key={k} className="small text-danger" data-testid={`sms-sorun-${k}`}>{t(`sablonlar.sorun.${k}`)}</div>);

  return (
    <SmsSayfa aktif="sablonlar">
      {mesaj && <Alert color="success" fade={false} toggle={() => setMesaj(null)}>{mesaj}</Alert>}
      {hata && <Alert color="danger" fade={false} data-testid="sms-sablon-hata">{hata}</Alert>}
      <Card>
        <CardBody>
          <p className="text-muted">{t('sablonlar.aciklama')}</p>
          {yukleniyor ? <div className="text-center py-4"><Spinner /></div> : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0" data-testid="sms-sablon-tablosu">
                <thead><tr><th>{t('sablonlar.sablon')}</th><th>{t('sablonlar.kategori')}</th><th>{t('sablonlar.turkce')}</th><th>{t('sablonlar.ingilizce')}</th><th>{t('hesaplar.durum')}</th><th /></tr></thead>
                <tbody>
                  {liste.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-4">{t('sablonlar.bos')}</td></tr>}
                  {liste.map((s) => (
                    <tr key={s.key} data-testid={`sms-sablon-${s.key}`}>
                      <td><div className="fw-semibold">{s.key}</div><div className="small text-muted">{s.description}</div></td>
                      <td><Badge color={s.category === 'OTP' ? 'info' : 'secondary'}>{s.category}</Badge></td>
                      <td className="small" style={{ maxWidth: 260 }}>{s.text_tr}</td>
                      <td className="small" style={{ maxWidth: 260 }}>{s.text_en ? s.text_en : <span className="text-muted">{t('sablonlar.ingilizceYok')}</span>}</td>
                      <td><Badge color={s.active === false ? 'secondary' : 'success'}>{s.active === false ? t('hesaplar.pasif') : t('hesaplar.etkin')}</Badge></td>
                      <td className="text-end"><Button size="sm" color="light" id={`sms-sablon-duzenle-${s.key}`} onClick={() => ac(s)}><i className="bx bx-edit" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={!!duzen} toggle={() => !kaydediyor && setDuzen(null)} size="lg" centered>
        {duzen && (
          <>
            <ModalHeader toggle={() => setDuzen(null)}>{t('sablonlar.duzenle', { anahtar: duzen.sablon.key })}</ModalHeader>
            <ModalBody>
              {formHatasi && <Alert color="danger" fade={false}>{formHatasi}</Alert>}
              <p className="text-muted small">{duzen.sablon.description}</p>
              <div className="mb-3" data-testid="sms-yer-tutucular">
                <span className="me-2 fw-semibold small">{t('sablonlar.yerTutucular')}:</span>
                {izinli.map((p) => (
                  <Button key={p.name} size="sm" color={zorunlu.includes(p.name) ? 'primary' : 'light'} outline={zorunlu.includes(p.name)} className="me-1 mb-1" id={`sms-yt-${p.name}`}
                    title={p.description || ''} onClick={() => ekle(p.name)}>{`{{${p.name}}}`}{zorunlu.includes(p.name) ? ' *' : ''}</Button>
                ))}
                <div className="form-text">{t('sablonlar.yerTutucuIpucu')}</div>
              </div>
              <div className="mb-3">
                <Label htmlFor="sms-sablon-tr">{t('sablonlar.turkce')}</Label>
                <Input id="sms-sablon-tr" type="textarea" rows={4} innerRef={trRef} value={duzen.tr} invalid={trSorun.length > 0}
                  onFocus={() => { sonOdak.current = 'tr'; }} onChange={(e) => setDuzen((d) => ({ ...d, tr: e.target.value }))} />
                <SmsMetinSayaci metin={duzen.tr} otp={otp} ornekler={ornekler} />
                {sorunMetni(trSorun)}
              </div>
              <div className="mb-3">
                <Label htmlFor="sms-sablon-en">{t('sablonlar.ingilizce')}</Label>
                <Input id="sms-sablon-en" type="textarea" rows={4} innerRef={enRef} value={duzen.en} invalid={enSorun.length > 0}
                  onFocus={() => { sonOdak.current = 'en'; }} onChange={(e) => setDuzen((d) => ({ ...d, en: e.target.value }))} />
                <SmsMetinSayaci metin={duzen.en} otp={otp} ornekler={ornekler} />
                {sorunMetni(enSorun)}
                <div className="form-text">{t('sablonlar.ingilizceBosOlur')}</div>
              </div>
              <div className="form-check form-switch mb-3">
                <Input className="form-check-input" type="checkbox" role="switch" id="sms-sablon-etkin" checked={duzen.etkin} onChange={(e) => setDuzen((d) => ({ ...d, etkin: e.target.checked }))} />
                <Label className="form-check-label" htmlFor="sms-sablon-etkin">{t('hesaplar.etkin')}</Label>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <Button size="sm" color="light" id="sms-onizle-tr" onClick={() => onizle('tr')}>{t('sablonlar.onizleTr')}</Button>
                <Button size="sm" color="light" id="sms-onizle-en" onClick={() => onizle('en')}>{t('sablonlar.onizleEn')}</Button>
              </div>
              {onizleme && (
                <Alert color={onizleme.hata ? 'danger' : 'light'} fade={false} className="mt-2 mb-0" data-testid="sms-onizleme">
                  {onizleme.yukleniyor ? <Spinner size="sm" /> : onizleme.hata ? onizleme.hata : (
                    <>
                      <div className="fw-semibold">{onizleme.sonuc.text}</div>
                      <div className="small text-muted">{t('sablonlar.onizlemeOlcu', { sayi: onizleme.sonuc.weighted ?? onizleme.sonuc.length, parca: onizleme.sonuc.parts })}</div>
                    </>
                  )}
                </Alert>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="light" onClick={() => setDuzen(null)}>{t('vazgec')}</Button>
              <Button color="primary" id="sms-sablon-kaydet" disabled={kaydediyor || kaydedilemez} onClick={kaydet}>{kaydediyor ? <Spinner size="sm" /> : t('kaydet')}</Button>
            </ModalFooter>
          </>
        )}
      </Modal>
    </SmsSayfa>
  );
};

export default SmsSablonlariPage;
